import { createStore } from 'vuex'
import axios from 'axios';

// Base for every backend call: the host the page is already being served from.
// One build therefore works on the Catalyst dev domain, the Catalyst prod
// domain, and the production domain (https://www.zohomea.com) with nothing to
// configure and no host list to keep up to date.
//
// Four actions used to carry a hardcoded { dev, prod, local } map and choose
// from it by sniffing window.location.hostname:
//
//    domainUrl.includes('development') ? dev : (includes('localhost') ? local : prod)
//
// with `prod` set to the Catalyst serverless host. On www.zohomea.com the
// hostname matches neither 'development' nor 'localhost', so it fell through to
// `prod` and sent every request cross-origin to a different domain. That needs
// CORS to work at all, and — more seriously for an auth-gated API — the browser
// would not attach the session cookie, so those calls would come back
// unauthorised rather than merely slow.
//
// Three other actions in this file already used window.location.origin, so this
// only brings the remaining four into line with what the majority already did.
//
// localhost is the one real exception: `npm run serve` puts the client on :8080
// while the Catalyst backend runs on :3000, and there is no devServer proxy, so
// that case genuinely needs an absolute host.
const LOCAL_API_ORIGIN = 'http://localhost:3000';

// Exported so services/authControl.js resolves the backend the same way rather
// than keeping a second copy of this rule.
export function apiBase() {
	const host = window.location.hostname;
	// Exact match, not includes(): 'localhost' as a substring would also match a
	// real hostname such as localhost.example.com.
	if (host === 'localhost' || host === '127.0.0.1') return LOCAL_API_ORIGIN;
	return window.location.origin;
}

// In-flight loadTemplateConfig calls, keyed by project id. Deliberately outside
// the store's state: these are promises, nothing renders from them, and making
// them reactive would only add churn. See the action for why they exist.
const templateConfigRequests = new Map();

export default createStore({
	state: {
		contactUsCliqChannelUrl: "https://cliq.zoho.in/company/60002085329/channels/zohomeaqueries",
		templateName: null,
		sectionList: [],
		projectId: "",
		projectName: "",
		projectVideoUrl: "",
		// projectId -> the fully processed template config, so revisiting a
		// solution costs nothing. Without this, every visit re-ran two sequential
		// network round trips plus the whole transform below, even for a solution
		// opened moments earlier. loadSolutions and loadCommonData already
		// short-circuit this way; this brings solution pages into line.
		templateConfigCache: {},
		loginUserDetails: {},
		imageFolderIds: {
			"Project_Logos": "20504000000041567",
			"Project_Images": "20504000000041548",
			"Project_Video_Demos": "20504000000041586",
			"Project_Key_Features": "20504000000041529",
			"About_Project_Images": "20504000000041605",
			"Project_PPT_Presentations": "20504000000041624",
			"Project_Assests": "20504000000041491",
			"Configuration": "20504000000041510",
			"Customer_Images": "20504000000041434"
		},
		solutionsPageTemplate: {},
		allCustomers: [],
		allSolutions: [],
		customerPageConfiguration: {},
		singleCustomerDetails: {},
		searchSolutionText: "",
		searchCustomerText: "",
		getInTouchFormData: {
			FullName: "",
			Email: "",
			Industry: "",
			YourRequirements: ""
		},
		getRegions: [],
		getIndustries: [],
		getSolutionCategories: [],
		productLogos: {
			"crm": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos_Without_Name/zoho_crm_logo.png",
			"mail": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos_Without_Name/zoho_mail_logo.png",
			"analytics": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos_Without_Name/zoho_analytics_logo.png",
			"people": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos_Without_Name/zoho_people_logo.png",
			"desk": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos_Without_Name/zoho_desk_logo.png",
			"projects": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos_Without_Name/zoho_projects_logo.png",
			"one": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos_Without_Name/zoho_one_logo.png",
			"creator": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos_Without_Name/zoho_creator_logo.png",
			"assist": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos_Without_Name/zoho_assist_logo.png",
			"backstage": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos_Without_Name/zoho_backstage_logo.png",
			"bigin": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos_Without_Name/zoho_bigin_logo.png",
			"books": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos_Without_Name/zoho_books_logo.png",
			"bookings": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos_Without_Name/zoho_bookings_logo.png",
			"campaigns": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos_Without_Name/zoho_campaigns_logo.png",
			"checkout": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos_Without_Name/zoho_checkout_logo.png",
			"cliq": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos_Without_Name/zoho_cliq_logo.png",
			"commerce": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos_Without_Name/zoho_commerce_logo.png",
			"connect": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos_Without_Name/zoho_connect_logo.png",
			"contracts": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos_Without_Name/zoho_contracts_logo.png",
			"dataprep": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos_Without_Name/zoho_dataprep_logo.png",
			"directory": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos_Without_Name/zoho_directory_logo.png",
			"expense": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos_Without_Name/zoho_expense_logo.png",
			"flow": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos_Without_Name/zoho_flow_logo.png",
			"forms": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos_Without_Name/zoho_forms_logo.png",
			"inventory": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos_Without_Name/zoho_inventory_logo.png",
			"invoice": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos_Without_Name/zoho_invoice_logo.png",
			"learn": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos_Without_Name/zoho_learn_logo.png",
			"marketing_automation": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos_Without_Name/zoho_marketing_automation_logo.png",
			"meeting": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos_Without_Name/zoho_meeting_logo.png",
			"notebooks": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos_Without_Name/zoho_notebooks_logo.png",
			"pagesense": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos_Without_Name/zoho_pagesense_logo.png",
			"recruit": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos_Without_Name/zoho_recruit_logo.png",
			"salesiq": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos_Without_Name/zoho_salesiq_logo.png",
			"sheet": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos_Without_Name/zoho_sheet_logo.png",
			"shifts": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos_Without_Name/zoho_shifts_logo.png",
			"show": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos_Without_Name/zoho_show_logo.png",
			"sign": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos_Without_Name/zoho_sign_logo.png",
			"socials": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos_Without_Name/zoho_socials_logo.png",
			"sprints": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos_Without_Name/zoho_sprints_logo.png",
			"subscriptions": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos_Without_Name/zoho_subscriptions_logo.png",
			"survey": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos_Without_Name/zoho_survey_logo.png",
			"team_inbox": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos_Without_Name/zoho_team_inbox_logo.png",
			"vault": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos_Without_Name/zoho_vault_logo.png",
			"workdrive": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos_Without_Name/zoho_workdrive_logo.png",
			"writer": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos_Without_Name/zoho_writer_logo.png"
		},
		productLogosWithNames: {
			"crm": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_CRM.svg",
			"crmplus": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_CRM_Plus.svg",
			"mail": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Mail.svg",
			"analytics": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Analytics.svg",
			"people": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_People.svg",
			"peopleplus": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_People_Plus.svg",
			"desk": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Desk.svg",
			"projects": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Projects.svg",
			"projectsplus": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Projects_Plus.svg",
			"one": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_One.svg",
			"creator": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Creator.svg",
			"creatorplus": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Creator_Plus.svg",
			"assist": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Analytics.svg",
			"backstage": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Backstage.svg",
			"bigin": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Bigin_by_Zoho_CRM.svg",
			"books": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Books.svg",
			"bookings": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Bookings.svg",
			"campaigns": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Campaigns.svg",
			"checkout": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Checkout.svg",
			"cliq": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Cliq.svg",
			"commerce": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Commerce.svg",
			"connect": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Connect.svg",
			"contracts": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Contracts.svg",
			"dataprep": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_DataPrep.svg",
			"directory": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Directory.svg",
			"expense": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Expense.svg",
			"flow": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Flow.svg",
			"forms": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Forms.svg",
			"inventory": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Inventory.svg",
			"invoice": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Invoice.svg",
			"learn": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Learn.svg",
			"lens": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Lens.svg",
			"mcp": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_MCP.svg",
			"marketing_automation": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Marketing_Automation.svg",
			"meeting": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Meeting.svg",
			"notebook": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Notebook.svg",
			"pagesense": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_PageSense.svg",
			"recruit": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Recruit.svg",
			"salesiq": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_SalesIQ.svg",
			"sheet": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Sheet.svg",
			"shifts": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Shifts.svg",
			"show": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Show.svg",
			"sign": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Sign.svg",
			"social": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Social.svg",
			"sprints": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Sprints.svg",
			"subscriptions": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Subscriptions.svg",
			"survey": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Survey.svg",
			"team_inbox": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Team_Inbox.svg",
			"vault": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Vault.svg",
			"workdrive": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_WorkDrive.svg",
			"writer": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Writer.svg",
			"catalyst": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Catalyst_by_Zoho.svg",
			"qntrl": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Qntrl_by_Zoho.svg",
			"toolkit": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Toolkit_by_Zoho_Mail.svg",
			"billing": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Billing.svg",
			"bugtracker": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_BugTracker.svg",
			"dap": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_DAP.svg",
			"domains": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Domains.svg",
			"erp": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_ERP.svg",
			"financeplus": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Finance_Plus.svg",
			"marketingplus": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Marketing_Plus.svg",
			"marketplace": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Marketplace.svg",
			"officeintegrator": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Office_Integrator.svg",
			"pos": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_POS.svg",
			"payroll": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Payroll.svg",
			"practice": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Practice.svg",
			"procurement": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Procurement.svg",
			"serviceplus": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Service_Plus.svg",
			"sites": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Sites.svg",
			"spend": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Spend.svg",
			"tables": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Tables.svg",
			"thrive": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Thrive.svg",
			"verticalstudio": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Vertical_Studio.svg",
			"webinar": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Webinar.svg",
			"workerly": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Workerly.svg",
			"workplace": "https://measiteassetpublic.zohostratus.in/Zoho_Product_Logos/Zoho_Workplace.svg"
		}
	},
	getters: {
	},
	mutations: {
		setTemplate(state, name) {
			state.templateName = name;
		},
		setSections(state, sections) {
			state.sectionList = sections;
		},
		setProjectId(state, id) {
			state.projectId = id;
		},
		setProjectName(state, name) {
			state.projectName = name;
		},
		setProjectVideoUrl(state, name) {
			state.projectVideoUrl = name;
		},
		setLoginUserDetails(state, userDetails) {
			state.loginUserDetails = userDetails;
		},
		setSolutionsPageTemplate(state, solutionsTemplate) {
			state.solutionsPageTemplate = solutionsTemplate;
		},
		setAllCustomers(state, customersList) {
			state.allCustomers = customersList;
		},
		setAllSolutions(state, allSolutions) {
			state.allSolutions = allSolutions;
		},
		setCustomerPageConfiguration(state, customerPageConfiguration) {
			state.customerPageConfiguration = customerPageConfiguration;
		},
		setSingleCustomerDetails(state, singleCustomerDetails) {
			state.singleCustomerDetails = singleCustomerDetails;
		},
		setSearchSolutionText(state, searchText) {
			state.searchSolutionText = searchText;
		},
		setSearchCustomerText(state, searchText) {
			state.searchCustomerText = searchText;
		},
		setGetInTouchFormData(state, getInTouchObjData) {
			state.getInTouchFormData = getInTouchObjData;
		},
		setRegions(state, regionList) {
			state.getRegions = regionList;
		},
		setIndustry(state, industryList) {
			state.getIndustries = industryList;
		},
		setSolutionCategories(state, solutionCategoryList) {
			state.getSolutionCategories = solutionCategoryList;
		},
		cacheTemplateConfig(state, { projectId, config }) {
			state.templateConfigCache[projectId] = config;
		},
	},
	actions: {
		/**
		 * Builds a project's template config into the store.
		 *
		 * Callers can overlap: the solutions list starts this speculatively when
		 * a card is hovered or pressed, and TemplateRenderer asks for the same
		 * project a moment later when the route resolves. Both get the SAME
		 * promise, so the second caller waits for the first request rather than
		 * firing a duplicate — and, more importantly, cannot proceed while the
		 * first is still mid-flight. Without that, the `setProjectId` below
		 * (which has to happen up front) would make the config look ready while
		 * sections and templateName were still those of the previous project.
		 */
		loadTemplateConfig({ dispatch }, projectId) {
			if (!projectId) return Promise.resolve();

			const key = String(projectId);
			const inFlight = templateConfigRequests.get(key);
			if (inFlight) return inFlight;

			const request = Promise.resolve(dispatch("buildTemplateConfig", projectId))
				.finally(() => { templateConfigRequests.delete(key); });

			templateConfigRequests.set(key, request);
			return request;
		},

		async buildTemplateConfig({ commit, state }, projectId) {
			if (projectId) {
				commit("setProjectId", projectId);

				// Already built once this session: replay it and skip both round
				// trips and the transform entirely. Cloned on the way out so a
				// component mutating sectionList cannot poison the cached copy for
				// the next visit.
				const cached = state.templateConfigCache[projectId];
				if (cached) {
					commit("setProjectVideoUrl", cached.projectVideoUrl);
					commit("setTemplate", cached.templateName);
					commit("setSections", JSON.parse(JSON.stringify(cached.sections)));
					commit("setProjectName", cached.projectName);
					return;
				}

				try {
					const requestUrldomain = apiBase();

					// The solutions list already holds every project row, and this
					// endpoint returns the same shape. When the visitor arrived from
					// /solutions the row is in hand, so the first of the two
					// sequential round trips is pure duplicate work — the second one
					// cannot start until it returns, so skipping it halves the wait.
					// Falls back to fetching for a direct hit on /solutions/{id},
					// where the list was never loaded, and for a row that somehow
					// lacks the template id.
					const fromList = (state.allSolutions || []).find(
						(s) => s && s.PROJECTS && String(s.PROJECTS.ROWID) === String(projectId)
					);

					let projectRow = fromList && fromList.PROJECTS && fromList.PROJECTS.ProjectTemplateJson
						? fromList
						: null;

					if (!projectRow) {
						const getProjectDetailsResp = await axios.get(`${requestUrldomain}/server/MEASiteBackendServer/api/project/${projectId}`);
						projectRow = getProjectDetailsResp.data[0];
					}

					const getVideoDemoUrlTag = projectRow.PROJECTS.ProjectVideoDemo;
					const getVideoDemoUrlThumbnail = projectRow.PROJECTS.ProjectVideoDemoThumbnail;
					const getBrochureImageId = projectRow.PROJECTS.ProjectBrochure;

					let getVideoDemoUrl = "";
					let getVideoDemoThumbnailUrl = "";
					if (getVideoDemoUrlTag) {
						if (getVideoDemoUrlTag.includes('<a') && getVideoDemoUrlTag.includes('</a>')) {
							const parser = new DOMParser();
							const doc = parser.parseFromString(getVideoDemoUrlTag, 'text/html');

							getVideoDemoUrl = doc.querySelector('a').href;
						} else {
							getVideoDemoUrl = getVideoDemoUrlTag;
						}
					}

					try {
						const parsedUrl = new URL(getVideoDemoUrl);
						if (parsedUrl.hostname.includes("youtube.com") && parsedUrl.searchParams.has("v")) {
							getVideoDemoThumbnailUrl = parsedUrl.searchParams.get("v");
						} else if (parsedUrl.hostname.includes("youtu.be")) {
							getVideoDemoThumbnailUrl = parsedUrl.pathname.slice(1);
						}
					} catch (e) {
						console.error("Invalid YouTube URL");
					}

					getVideoDemoThumbnailUrl = `https://img.youtube.com/vi/${getVideoDemoThumbnailUrl}/hqdefault.jpg`;

					commit("setProjectVideoUrl", getVideoDemoUrl);

					const getTemplateJsonId = projectRow.PROJECTS.ProjectTemplateJson;
					const getTemplateJson = await axios.get(`${requestUrldomain}/server/MEASiteBackendServer/api/file/20504000000041510/${getTemplateJsonId}`);

					if (getTemplateJson.data) {
						let templateData = getTemplateJson.data;
						if (typeof templateData === 'string') {
							try {
								templateData = JSON.parse(templateData);
							} catch (e) {
								console.error("Failed to parse template JSON", e);
							}
						}

						for (let index = 0; index < templateData.sections.length; index++) {
							if (templateData.sections[index].name.includes("AboutProjectSection")) {
								if (getBrochureImageId) {
									templateData.sections[index].data.aboutProject["aboutProjectBrochureImage"] = {
										label: "About Project Brochure Image",
										required: false,
										type: "singleline",
										value: getBrochureImageId
									};
								}
								if (!templateData.sections[index].data.aboutProject["aboutProjectVideoDemoUrl"]) {
									templateData.sections[index].data.aboutProject["aboutProjectVideoDemoUrl"] = {
										label: "About Project Video Demo URL",
										required: false,
										type: "singleline",
										value: getVideoDemoUrl
									};

									templateData.sections[index].data.aboutProject["aboutProjectVideoDemoUrlThumbnail"] = {
										label: "About Project Video Demo URL Thumbnail",
										required: false,
										type: "singleline",
										value: getVideoDemoUrlThumbnail
									};
								}
							}
							if (templateData.sections[index].name === "PresentationSection") {
								const getPresentationList = templateData.sections[index].data.projectPresentations["presentationList"].value;

								for (let sub_index = 0; sub_index < getPresentationList.length; sub_index++) {
									if (getPresentationList[sub_index]["presentationUrl"].value) {
										let getPresentationUrl = "";
										let getPresentationUrlTag = getPresentationList[sub_index]["presentationUrl"].value;

										if (getPresentationUrlTag) {
											if (getPresentationUrlTag.includes('<a') && getPresentationUrlTag.includes('</a>')) {
												try {
													const parserPresentation = new DOMParser();
													const docPresentation = parserPresentation.parseFromString(getPresentationUrlTag, 'text/html');
													const anchor = docPresentation.querySelector('a');
													getPresentationUrl = anchor ? anchor.href : "";
												} catch (err) {
													console.warn("Error parsing presentation URL tag:", err);
												}
											} else {
												getPresentationUrl = getPresentationUrlTag;
											}
										}

										templateData.sections[index].data.projectPresentations["presentationList"].value[sub_index]["presentationUrl"].value = getPresentationUrl;
									}
								}
							}
						}

						commit("setTemplate", templateData.templateName);
						commit("setSections", templateData.sections);
						commit("setProjectName", templateData.projectDetails.ProjectName);

						// Cached only after a fully successful build, so a partial
						// failure is retried next time rather than pinned. Stored as
						// a clone for the same reason it is cloned on read.
						commit("cacheTemplateConfig", {
							projectId,
							config: {
								templateName: templateData.templateName,
								sections: JSON.parse(JSON.stringify(templateData.sections)),
								projectName: templateData.projectDetails.ProjectName,
								projectVideoUrl: getVideoDemoUrl
							}
						});
					}
				} catch (e) {
					console.error("Error in loadTemplateConfig:", e);
				}
			}
		},
		async loadCustomerData({ commit }, customerId) {
			if (customerId) {
				const requestUrldomain = apiBase();
				try {
					const getCustomerResp = await axios.get(`${requestUrldomain}/server/MEASiteBackendServer/api/customer/${customerId}`);
					if (getCustomerResp.data.length > 0) {
						commit("setSingleCustomerDetails", getCustomerResp.data[0].CUSTOMERS);
					} else {
						console.warn(`No customer found for id ${customerId}`);
					}
				} catch (e) {
					// Was an empty catch. A failure here leaves every binding on the
					// customer page blank with nothing in the console to explain it,
					// which is exactly how the missing dispatch went unnoticed.
					console.error("Error loading customer data: ", e);
				}
			}
		},
		async loadSolutionPageTemplate({ commit }) {
			const getTemplateRes = await axios.get("./template-solution-config.json");
			commit("setSolutionsPageTemplate", getTemplateRes.data);
		},
		async loadSolutions({ commit, state }) {
			try {
				const requestUrldomain = apiBase();

				if (state.allSolutions.length === 0) {
					const resp = await axios.get(requestUrldomain + '/server/MEASiteBackendServer/api/solution');
					commit('setAllSolutions', resp.data);
				}
			} catch (e) {
				console.error("Error loading solutions: ", e);
			}
		},
		async loadCustomers({ commit, state }) {
			try {
				const requestUrldomain = apiBase();

				if (state.allCustomers.length === 0) {
					const resp = await axios.get(requestUrldomain + '/server/MEASiteBackendServer/api/customer');
					commit('setAllCustomers', resp.data);
				}
			} catch (e) {
				console.error("Error loading customers: ", e);
			}
		},
		async loadCustomerListPageConfig({ commit, state }) {
			try {
				const requestUrldomain = apiBase();

				if (Object.keys(state.customerPageConfiguration).length === 0) {
					const resp = await axios.get(requestUrldomain + '/server/MEASiteBackendServer/api/customerlistpageconfig');
					commit('setCustomerPageConfiguration', resp.data);
				}
			} catch (e) {
				console.error("Error loading customer config: ", e);
			}
		},
		async loadCommonData({ commit, state }) {
			try {
				const requestUrldomain = apiBase();

				const apiRequests = [];
				if (state.getRegions.length === 0) {
					apiRequests.push(axios.get(requestUrldomain + '/server/MEASiteBackendServer/api/region').then(resp => commit('setRegions', resp.data)).catch(e => console.error(e)));
				}
				if (state.getIndustries.length === 0) {
					apiRequests.push(axios.get(requestUrldomain + '/server/MEASiteBackendServer/api/industry').then(resp => commit('setIndustry', resp.data)).catch(e => console.error(e)));
				}
				if (state.getSolutionCategories.length === 0) {
					apiRequests.push(axios.get(requestUrldomain + '/server/MEASiteBackendServer/api/solutioncategory').then(resp => commit('setSolutionCategories', resp.data)).catch(e => console.error(e)));
				}
				await Promise.allSettled(apiRequests);
			} catch (e) {
				console.error("Error loading common data: ", e);
			}
		},
		async addNewLead({ state, commit }) {

			try {
				const addNewLeadResp = await axios.post('/server/MEASiteBackendServer/api/lead', state.getInTouchFormData);
				commit('setGetInTouchFormData', {
					FullName: "",
					Email: "",
					Industry: "",
					YourRequirements: ""
				})
				return addNewLeadResp;
			} catch (e) {
				return e;
			}
		}
	},
	modules: {
	}
});
